// i18n.ts — инициализация react-i18next для сайта EdPad Учитель.
//
// Языки: ru (источник, fallback), en, es, ka. Выбор хранится в localStorage
// (ключ edpad_teacher_lang); при первом визите — по navigator.language. Строки
// лежат в src/locales/<lng>.json (namespace по умолчанию — translation).
// Паттерн повторяет EdPad Class (platform/web/src/i18n.js).
//
// Загрузка: ru (основной язык и fallback) — в главном бандле, чтобы первый
// экран рисовался без ожидания; en/es/ka — отдельными чанками через dynamic
// import, только когда выбран соответствующий язык (≈130 KB каждая).
// main.tsx ждёт i18nReady перед первым рендером.
import i18n, { type BackendModule } from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import ru from './locales/ru.json'

export const SUPPORTED_LANGS = ['ru', 'en', 'es', 'ka'] as const
export type Lang = (typeof SUPPORTED_LANGS)[number]

// Подписи языков — на самих языках (эндонимы), узнаваемы независимо от локали.
export const LANG_LABELS: Record<Lang, string> = {
  ru: 'Русский',
  en: 'English',
  es: 'Español',
  ka: 'ქართული',
}

type LocaleJson = Record<string, unknown>
const LAZY_LOCALES: Record<Exclude<Lang, 'ru'>, () => Promise<{ default: LocaleJson }>> = {
  en: () => import('./locales/en.json'),
  es: () => import('./locales/es.json'),
  ka: () => import('./locales/ka.json'),
}

/** Минимальный i18next-backend: локаль из отдельного чанка Vite. */
const lazyLocaleBackend: BackendModule = {
  type: 'backend',
  init() {},
  read(language, _namespace, callback) {
    const loader = LAZY_LOCALES[language as keyof typeof LAZY_LOCALES]
    if (!loader) {
      callback(null, {})
      return
    }
    loader()
      .then((m) => callback(null, m.default))
      .catch((err: unknown) => callback(err instanceof Error ? err : new Error(String(err)), null))
  },
}

export const i18nReady = i18n
  .use(lazyLocaleBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      ru: { translation: ru },
    },
    // ru встроен, остальные языки догружаются backend'ом
    partialBundledLanguages: true,
    fallbackLng: 'ru',
    supportedLngs: SUPPORTED_LANGS as unknown as string[],
    nonExplicitSupportedLngs: true, // en-US → en
    load: 'languageOnly',
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: 'edpad_teacher_lang',
      caches: ['localStorage'],
    },
    interpolation: { escapeValue: false }, // React сам экранирует
  })

export default i18n
